import { Component, EventEmitter, Output } from "@angular/core";
// eslint:disable-next-line:no-shadowed-variable
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { environment } from "../../../../environments/environment";
import { SECTORS } from "../../constants/sectors";
import { isEnglish } from "../../current-language";
import { IContributionsQuoteDTO } from "../../models/IContributionsQuote";
import {
  IAreaSelectionDTO,
  IContributionsQuoteFormDTO,
  ILotNumbersDTO,
} from "../../models/IContributionsQuoteForm";
import { ISector } from "../../models/ITerritory";
import { SocialHousingService } from "../../services/social-housing.service";

const $ = (window as any).$;
// eslint:disable-next-line:max-line-length
export const MINIMUM_LIVING_AREA_18001_SQUARE_METER = 18001;
const SECTORS_URL =
  "https://experience.arcgis.com/experience/9417efdee2c74eec8fd9ead90ed52263/";

export enum ENUM_ERROR_TYPE {
  AREA = "Area",
  DWELLINGS = "dwellings",
  LOT_NUMBERS = "lot numbers",
}

@Component({
  selector: "app-search-location",
  templateUrl: "./search-location.component.html",
  styleUrls: ["./search-location.component.scss"],
})
export class SearchLocationComponent {
  @Output() public resultsChanged: EventEmitter<IContributionsQuoteDTO> =
    new EventEmitter<IContributionsQuoteDTO>();
  @Output() public calculError: EventEmitter<string> =
    new EventEmitter<string>();
  public formCalcul: UntypedFormGroup;
  public sectors: ISector[] = SECTORS;
  public isCalculating = false;

  public isShowInfoModal = false;
  public infoModalTitle = "";
  public imageData: any;

  public isEn = isEnglish();
  public usageLink = isEnglish()
    ? environment.linkUsageEn
    : environment.linkUsageFr;

  // eslint:disable-next-line:no-empty
  constructor(
    private fb: UntypedFormBuilder,
    private housingService: SocialHousingService,
  ) {
    this.formCalcul = this.fb.group({
      searchType: ["byCadastre"],
      noCadastre: ["", Validators.required],
      sector: [""],
      address: [""],
      superficie: ["", [Validators.required, Validators.pattern(/^[0-9]*$/)]],
      nbAppartement: ["", Validators.required],
    });
  }

  public get searchType() {
    return this.formCalcul.get("searchType");
  }

  public get noCadastre() {
    return this.formCalcul.get("noCadastre");
  }

  public get sector() {
    return this.formCalcul.get("sector");
  }

  public get sectorName() {
    const sector = this.sector.value;
    if (!sector) {
      return "";
    }
    const sectorInfo = this.sectors.find((x) => x.code === sector);
    return this.isEn ? sectorInfo.name.en : sectorInfo.name.fr;
  }

  public get address() {
    return this.formCalcul.get("address");
  }

  public get superficie() {
    return this.formCalcul.get("superficie");
  }

  public get nbAppartement() {
    return this.formCalcul.get("nbAppartement");
  }

  public showPopOver(target: HTMLElement) {
    $(target).popover();
  }

  public showInfoModal(type: string) {
    window.open(SECTORS_URL, "_blank");
  }

  public handleInfoModalResponse() {
    this.imageData = null;
    this.infoModalTitle = "";
    this.isShowInfoModal = false;
  }

  public onChangeType(event: any) {
    this.formCalcul.controls.noCadastre.setValue("");
    this.formCalcul.controls.address.setValue("");
    this.formCalcul.controls.sector.setValue("");

    if (event.target.value === "byCadastre") {
      this.formCalcul.controls.noCadastre.setValidators([Validators.required]);
      this.formCalcul.controls.noCadastre.updateValueAndValidity();
      this.formCalcul.controls.address.setValidators([]);
      this.formCalcul.controls.address.updateValueAndValidity();
      this.formCalcul.controls.sector.setValidators([]);
      this.formCalcul.controls.sector.updateValueAndValidity();
    } else if (event.target.value === "byTerritory") {
      this.formCalcul.controls.noCadastre.setValidators([]);
      this.formCalcul.controls.noCadastre.updateValueAndValidity();
      this.formCalcul.controls.address.setValidators([]);
      this.formCalcul.controls.address.updateValueAndValidity();
      this.formCalcul.controls.sector.setValidators([Validators.required]);
      this.formCalcul.controls.sector.updateValueAndValidity();
    }
    this.formCalcul.updateValueAndValidity();
  }

  public onSectorClick(sCode: string) {
    this.formCalcul.updateValueAndValidity();
  }

  public async handleCalculClick() {
    this.isCalculating = true;
    this.housingService.lastSearchResult = null;
    this.resultsChanged.emit();
    this.calculError.emit();

    const validationError = this.checkForError();
    if (validationError) {
      this.calculError.emit(validationError);
      this.isCalculating = false;
      return;
    }

    try {
      let lot: ILotNumbersDTO;
      let area: IAreaSelectionDTO;
      const sec = this.sectors.find((x) => x.code === this.sector.value);
      if (this.searchType.value === "byCadastre") {
        lot = {
          kind: "lotNumbers",
          lotNumbers: [+this.formCalcul.value.noCadastre],
        };
      } else {
        area = {
          kind: "areaSelection",
          applicationTerritoryName: sec.territory,
          valueSectorName: sec.code === "6" ? "5" : sec.code,
        };
      }

      const form: IContributionsQuoteFormDTO = {
        location: lot ? lot : area,
        addedLivingArea: +this.formCalcul.value.superficie,
        addedUnits: +this.formCalcul.value.nbAppartement,
      };
      const results = await this.housingService.calculateSocialHousing(form);
      this.housingService.lastSearchResult = results;
      this.resultsChanged.emit(results);
    } catch (e) {
      const message = e.error.error.message;
      this.calculError.emit(message.logMessage || message);
    } finally {
      this.isCalculating = false;
    }
  }

  public resetCalculator() {
    this.formCalcul.patchValue({
      searchType: "byCadastre",
      noCadastre: "",
      sector: "",
      address: "",
      superficie: "",
      nbAppartement: "",
    });
    this.formCalcul.updateValueAndValidity();
    this.resultsChanged.emit(null);
    window.scroll(0, 0);
  }

  private checkForError(): string {
    let error = "";

    if (
      +this.formCalcul.value.superficie < MINIMUM_LIVING_AREA_18001_SQUARE_METER
    ) {
      error = ENUM_ERROR_TYPE.AREA;
    } else if (+this.formCalcul.value.nbAppartement === 0) {
      error = ENUM_ERROR_TYPE.DWELLINGS;
    }
    return error;
  }
}
